import {
  getQueryKey,
  useApi,
  useDeleteQuery,
  useGetQuery,
  usePatchQuery,
  usePostQuery,
  useRouteParams
} from "@quortex/front-common"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { RichOttEndpoint } from "@/App/play/App/Main/Pool/endpoints/ottEndpoints/OttEndpointList/types"
import type { OttEndpointParams, PoolParams } from "./types"

interface BatchVariables {
  endpoints: Array<RichOttEndpoint>
  enabled: boolean
  headers?: Record<string, string>
}

//--------------------------------------------------------------------------------------------------
// URLs
//--------------------------------------------------------------------------------------------------

/** @returns /pools/${poolUuid}/ott_endpoints/?org=${orgUuid} */
export const getOttEndpointListUrl = ({ orgUuid, poolUuid }: Required<PoolParams>) =>
  `/pools/${poolUuid}/ott_endpoints/?org=${orgUuid}`

/** @returns /pools/${poolUuid}/ott_endpoints/${ottEndpointUuid}/?org=${orgUuid} */
export const getOttEndpointUrl = ({
  orgUuid,
  poolUuid,
  ottEndpointUuid
}: Required<OttEndpointParams>) =>
  `/pools/${poolUuid}/ott_endpoints/${ottEndpointUuid}/?org=${orgUuid}`

//--------------------------------------------------------------------------------------------------
// Queries & mutations
//--------------------------------------------------------------------------------------------------

export const useOttEndpointList = (params?: PoolParams) => {
  const mergedParams = useRouteParams(params)
  const listUrl = getOttEndpointListUrl(mergedParams)

  return useGetQuery<api.ott.PaginatedOttEndpointList>("ott", listUrl)
}

export const useOttEndpoint = (params?: OttEndpointParams) => {
  const mergedParams = useRouteParams(params)
  const url = getOttEndpointUrl(mergedParams)

  return useGetQuery<api.ott.OttEndpoint>("ott", url)
}

export const useAddOttEndpoint = (params?: PoolParams) => {
  const mergedParams = useRouteParams(params)
  const listUrl = getOttEndpointListUrl(mergedParams)

  type TRequest = api.ott.OttEndpointRequest
  type TResponse = api.ott.OttEndpoint

  return usePostQuery<TRequest, TResponse>("ott", listUrl, [listUrl])
}

export const useUpdateOttEndpoint = (params?: OttEndpointParams) => {
  const mergedParams = useRouteParams(params)
  const url = getOttEndpointUrl(mergedParams)
  const listUrl = getOttEndpointListUrl(mergedParams)

  type TRequest = api.ott.OttEndpointUpdateRequest
  type TResponse = api.ott.OttEndpoint

  return usePatchQuery<TRequest, TResponse>("ott", url, [listUrl])
}

export const useDeleteOttEndpoint = (params?: OttEndpointParams) => {
  const mergedParams = useRouteParams(params)
  const url = getOttEndpointUrl(mergedParams)
  const listUrl = getOttEndpointListUrl(mergedParams)

  return useDeleteQuery("ott", url, [listUrl])
}

export const useBatchUpdateOttEndpoints = ({
  orgUuid,
  poolUuid
}: {
  orgUuid?: string
  poolUuid?: string
}) => {
  const queryClient = useQueryClient()
  const { jsonPatch } = useApi("ott")

  return useMutation<void, Error, BatchVariables>({
    mutationFn: async ({ endpoints, enabled }) => {
      if (!orgUuid || !poolUuid) {
        console.warn("Skipping OTT Endpoints batch update: missing orgUuid or poolUuid")
        return
      }
      await Promise.all(
        endpoints.map(async endpoint => {
          const params = { orgUuid, ottEndpointUuid: endpoint.uuid, poolUuid }
          const url = getOttEndpointUrl(params)
          await jsonPatch(url, { enabled })
        })
      )
    },
    onError: error => {
      console.error("Batch update OTT Endpoints failed:", error)
    },
    onSuccess: async () => {
      if (!orgUuid || !poolUuid) {
        return
      }
      const listUrl = getOttEndpointListUrl({ orgUuid, poolUuid })
      await queryClient.refetchQueries({ queryKey: getQueryKey("ott", listUrl) })
    }
  })
}
